import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { Button, Card, Col, Empty, Modal, Row, Space, Statistic, Table, Tag, Tooltip, message } from 'antd'
import PageTitle from 'components/PageTitle'
import PaginationContainer from '../../components/Forms/Pagination'
import { formatUrl, getRequest, growthRunUrl, growthRunsUrl, growthSpendUrl, postRequest } from '../../config'
import { JOB_LABELS, formatUsd, runningRun } from './utils'

const POLL_MS = 5000
const POLL_LIMIT_MS = 3 * 60 * 1000

const RUN_BUTTONS = [
  { job: 'digest_full', label: 'Full digest', confirm: 'Search now and send every upcoming opportunity?' },
  { job: 'digest_new', label: 'New-only digest', confirm: 'Search now and send only what is new?' },
  { job: 'review', label: 'Weekly review', confirm: 'Write and send last week\'s look-back now?' }
]

// What the bot did and what it cost (spec: 0042_growth_bot Req 4).
const RunsPage = () => {
  const [runs, setRuns] = useState([])
  const [meta, setMeta] = useState({})
  const [spend, setSpend] = useState(null)
  const [loading, setLoading] = useState(false)
  const pollStarted = useRef(null)

  const loadRuns = async (pageUrl = null) => {
    const response = await getRequest(pageUrl ? formatUrl(pageUrl) : growthRunsUrl, 'runs', setLoading)
    if (response.success) {
      setRuns(response.data.results || [])
      setMeta({ ...response.data })
    }
  }

  const loadSpend = async () => {
    const response = await getRequest(growthSpendUrl, 'spend', () => {})
    if (response.success) setSpend(response.data)
  }

  useEffect(() => {
    loadRuns()
    loadSpend()
  }, [])

  // Refresh while a run is going, for up to three minutes.
  useEffect(() => {
    if (!runningRun(runs)) {
      pollStarted.current = null
      return undefined
    }
    pollStarted.current = pollStarted.current || Date.now()
    if (Date.now() - pollStarted.current > POLL_LIMIT_MS) return undefined
    const timer = setTimeout(() => {
      getRequest(growthRunsUrl, 'runs', () => {}).then(response => {
        if (response.success) setRuns(response.data.results || [])
      })
    }, POLL_MS)
    return () => clearTimeout(timer)
  }, [runs])

  const runNow = ({ job, confirm }) => {
    Modal.confirm({
      title: confirm,
      okText: 'Run now',
      onOk: async () => {
        const response = await postRequest(growthRunUrl, { job }, 'run', () => {})
        if (response.success) {
          message.success('Queued. It will appear in Telegram when done.')
          loadRuns()
        } else {
          const detail = response.error?.response?.data?.detail
          if (detail) message.error(detail)
        }
      }
    })
  }

  /* eslint-disable react/display-name */
  const runColumns = [
    { title: 'Job', key: 'job', render: (_, r) => `${JOB_LABELS[r.job] || r.job}${r.mode ? ` (${r.mode})` : ''}` },
    { title: 'Started', key: 'at', render: (_, r) => moment(r.createdAt).format('D MMM, HH:mm') },
    { title: 'Took', key: 'took', render: (_, r) => (r.seconds != null ? `${r.seconds}s` : '—') },
    {
      title: 'Outcome',
      key: 'ok',
      render: (_, r) => {
        if (r.running) return <Tag color="blue">Running</Tag>
        if (r.ok) return <Tag color="green">Done</Tag>
        return (
          <Tooltip title={r.error}>
            <Tag color="red">Failed</Tag>
          </Tooltip>
        )
      }
    },
    { title: 'Found', dataIndex: 'found', key: 'found' },
    { title: 'New', dataIndex: 'new', key: 'new' },
    { title: 'Sent', dataIndex: 'sent', key: 'sent' }
  ]
  const spendColumns = [
    { title: 'From', dataIndex: 'period', key: 'period' },
    { title: 'AI calls', dataIndex: 'calls', key: 'calls' },
    { title: 'Web searches', dataIndex: 'searches', key: 'searches' },
    { title: 'Cost', key: 'cost', render: (_, r) => formatUsd(r.costUsd) }
  ]
  /* eslint-enable react/display-name */

  return (
    <div className="container-fluid">
      <PageTitle title="Growth · Runs & spend" />
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card
          title="Runs"
          extra={
            <Space wrap>
              {RUN_BUTTONS.map(b => (
                <Button key={b.job} onClick={() => runNow(b)}>
                  {b.label}
                </Button>
              ))}
            </Space>
          }
        >
          <Table
            rowKey="id"
            loading={loading}
            dataSource={runs}
            columns={runColumns}
            pagination={false}
            scroll={{ x: 700 }}
            locale={{ emptyText: <Empty description="No runs yet" /> }}
          />
          {runs.length ? <PaginationContainer meta={meta} getData={loadRuns} /> : null}
        </Card>

        <Card title="AI spend" loading={!spend}>
          {spend ? (
            <>
              <Statistic title="All time (estimate)" value={formatUsd(spend.totalUsd)} style={{ marginBottom: 16 }} />
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Table
                    title={() => 'By week'}
                    rowKey="period"
                    size="small"
                    pagination={false}
                    dataSource={spend.byWeek}
                    columns={spendColumns}
                  />
                </Col>
                <Col xs={24} lg={12}>
                  <Table
                    title={() => 'By month'}
                    rowKey="period"
                    size="small"
                    pagination={false}
                    dataSource={spend.byMonth}
                    columns={spendColumns}
                  />
                </Col>
              </Row>
            </>
          ) : null}
        </Card>
      </Space>
    </div>
  )
}

export default RunsPage
